import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listSystemSettings } from "@/api/lookups";
import type { SystemSettingResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { SettingForm } from "@/components/registries/SettingForm";

export function SettingsAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SystemSettingResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const settings = useQuery({
    queryKey: ["system-settings", "list"],
    queryFn: () => listSystemSettings(),
  });

  const columns: ColumnDef<SystemSettingResponse, unknown>[] = useMemo(
    () => [
      {
        id: "key",
        header: t("adminSettings.columns.key"),
        cell: (info) => info.row.original.key,
      },
      {
        id: "value",
        header: t("adminSettings.columns.value"),
        cell: (info) => (
          <code className="text-xs">
            {JSON.stringify(info.row.original.value)}
          </code>
        ),
      },
      {
        id: "updated_at",
        header: t("adminSettings.columns.updatedAt"),
        cell: (info) =>
          new Date(info.row.original.updated_at).toLocaleString("ru"),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(info.row.original)}
            data-testid={`edit-setting-${info.row.original.key}`}
          >
            {t("common.edit")}
          </Button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">{t("adminSettings.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("adminSettings.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="open-create-setting"
        >
          {t("common.create")}
        </Button>
      </header>
      <DataTable
        data={settings.data ?? []}
        columns={columns}
        testId="settings-table"
        isLoading={settings.isLoading}
      />
      <SettingForm
        open={creating || editing !== null}
        existing={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        afterSubmit={() =>
          queryClient.invalidateQueries({ queryKey: ["system-settings"] })
        }
      />
    </div>
  );
}
